// App-store rating helper.
//
// Two ways to ask for a rating:
//   1. requestInAppReview() — the native OS "rate this app" sheet (StoreKit on
//      iOS, Play In-App Review on Android). The OS decides whether to actually
//      show it (both stores throttle heavily) and never reports the outcome.
//   2. openStoreListing() — deep-links straight to the store listing / write-a-
//      review page. Always works; used for the explicit "Rate the99" button.
//
// maybePromptForReview() wraps #1 with a frequency cap so a "happy moment"
// (e.g. finishing a trip) can safely call it on every occurrence without
// nagging. All storage/plugin access is guarded and never throws into the UI.

import { Capacitor } from "@capacitor/core";

// Android application id (see capacitor.config.ts `appId` / build.gradle).
export const ANDROID_PACKAGE_ID = "app.the99";

// TODO: replace with the real numeric App Store id once the iOS app is
// published (also update the placeholder in public/get.html).
export const APPLE_APP_ID = "0000000000";

const IS_NATIVE = (() => {
  try {
    return Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
})();

function getPlatform() {
  try {
    return Capacitor?.getPlatform?.() || "web";
  } catch {
    return "web";
  }
}

// ── Store deep links ───────────────────────────────────────────────────────
export function storeListingUrl(platform = getPlatform()) {
  if (platform === "ios") {
    return `https://apps.apple.com/app/id${APPLE_APP_ID}?action=write-review`;
  }
  // Android + web both fall back to the Play listing.
  return `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE_ID}`;
}

/**
 * Open the platform store listing (write-review page on iOS). This is an
 * explicit user action, so — unlike the throttled native prompt — it always
 * takes the user somewhere.
 */
export function openStoreListing() {
  const platform = getPlatform();
  // Native Android: `market://` opens the Play Store app directly.
  if (IS_NATIVE && platform === "android") {
    try {
      window.open(`market://details?id=${ANDROID_PACKAGE_ID}`, "_system");
      return;
    } catch {
      /* fall through to the https listing */
    }
  }
  const url = storeListingUrl(platform);
  try {
    window.open(url, IS_NATIVE ? "_system" : "_blank", "noopener,noreferrer");
  } catch {
    window.location.href = url;
  }
}

// ── Native in-app review ───────────────────────────────────────────────────
/**
 * Ask the OS to present its native rating sheet. No-op on web (falls back to
 * opening the store listing). Never throws.
 */
export async function requestInAppReview() {
  if (!IS_NATIVE) {
    openStoreListing();
    return;
  }
  try {
    const { InAppReview } = await import("@capacitor-community/in-app-review");
    await InAppReview.requestReview();
  } catch (err) {
    console.warn("[appReview] requestReview failed:", err?.message || err);
  }
}

// ── Frequency-capped "happy moment" prompt ─────────────────────────────────
const LS_LAST = "the99.review.lastPromptAt";
const LS_COUNT = "the99.review.promptCount";
const LS_OPTOUT = "the99.review.optOut";
const MIN_GAP_MS = 90 * 24 * 60 * 60 * 1000; // at most ~once per quarter
const MAX_PROMPTS = 3; // lifetime cap (aligns with iOS's ~3/year limit)

function readNum(key) {
  try {
    return Number(localStorage.getItem(key)) || 0;
  } catch {
    return 0;
  }
}

/**
 * Call at a positive moment (e.g. finishing a trip). Native-only and throttled:
 * at most once per ~quarter, up to a lifetime cap. Returns true if a prompt was
 * actually requested (the OS may still choose not to display it).
 */
export async function maybePromptForReview() {
  if (!IS_NATIVE) return false;
  try {
    if (localStorage.getItem(LS_OPTOUT) === "1") return false;
    const count = readNum(LS_COUNT);
    if (count >= MAX_PROMPTS) return false;
    const last = readNum(LS_LAST);
    if (last && Date.now() - last < MIN_GAP_MS) return false;
    localStorage.setItem(LS_LAST, String(Date.now()));
    localStorage.setItem(LS_COUNT, String(count + 1));
  } catch {
    // localStorage blocked — skip rather than risk nagging every time.
    return false;
  }
  await requestInAppReview();
  return true;
}
